import { isEmpty } from 'lodash';
import { User, UserManager, UserManagerSettings } from 'oidc-client';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { store } from 'src/common/redux/configure-store';
import { LoginActions } from 'src/common/redux/reducers/login-reducer';
import ElectronHelper from '../common/electron/ElectronHelper';
import { RetailElectronAPI } from '../common/electron/RetailElectronAPI';
import { LocalStorage } from '../common/helpers/local-storage';
import { getMetaData, getOpenIdConfig } from '../xlr-configuration/pos-service-type-utils';
import { IFrameNavigator } from './navigators/iframe-navigator';

export const DEFAULT_REACT_APP_REDIRECT_URL = '/react-nest-client/auth/callback';
export const DEFAULT_REACT_APP_LOGOFF_REDIRECT_URL = '/react-nest-client/logout/callback';
const DEFAULT_TOKEN_EXPIRATION_RENEWAL_TIME = 170;

interface AuthContextType {
  loading: boolean;
  signinPopup: (saveToken?: boolean) => Promise<User>;
  signinPopupCallback: () => Promise<User | undefined>;
  signOutCallback: (url?: string) => Promise<void>;
  isAuthenticated: () => boolean;
  logout: () => Promise<void>;
  getUser: () => Promise<string>;
}

const AuthContext = React.createContext<AuthContextType>({
  loading: true,
  signinPopup: () => Promise.reject(),
  signinPopupCallback: () => Promise.reject(),
  signOutCallback: () => Promise.reject(),
  isAuthenticated: () => false,
  logout: () => Promise.reject(),
  getUser: () => Promise.resolve(''),
});

export function useAuthContext() {
  return useContext(AuthContext);
}

const getAuthConfig = async () => {
  if (!isEmpty(LocalStorage.getOpenIdConfig())) {
    return LocalStorage.getOpenIdConfig();
  } else {
    return getOpenIdConfig().then(config => {
      LocalStorage.setOpenIdConfig(config);
      return config;
    });
  }
};

export const AuthProviderWrapper: React.FC<any> = ({ children }): JSX.Element => {
  const { EDGE_IAM } = getMetaData().xlrConfig;
  return EDGE_IAM ? <AuthProvider>{children}</AuthProvider> : <>{children}</>;
};

export const AuthProvider: React.FC<any> = ({ children }): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [userManager, setUserManager] = useState<UserManager>(new UserManager({}));

  useEffect(() => {
    getAuthConfig().then(config => {
      const {
        issuer,
        authorization_endpoint,
        end_session_endpoint,
        revocation_endpoint,
        token_endpoint,
        // scopes_supported,
      } = config;

      const {
        REACT_APP_IDENTITY_CLIENT_ID,
        REACT_APP_IDENTITY_CLIENT_SECRET,
        REACT_APP_REDIRECT_URL,
        REACT_APP_LOGOFF_REDIRECT_URL,
        REACT_APP_TOKEN_EXPIRATION_RENEWAL_TIME,
      } = ElectronHelper.isElectron() ? RetailElectronAPI.getEnvironmentVariables() : process.env;

      const redirectUri = new URL(
        REACT_APP_REDIRECT_URL || DEFAULT_REACT_APP_REDIRECT_URL,
        window.origin,
      ).toString();
      const logoffRedirectUri = new URL(
        REACT_APP_LOGOFF_REDIRECT_URL || DEFAULT_REACT_APP_LOGOFF_REDIRECT_URL,
        window.origin,
      ).toString();

      const envRenewalTime = Number(REACT_APP_TOKEN_EXPIRATION_RENEWAL_TIME);
      const tokenRenewalTime: number = !isNaN(envRenewalTime)
        ? envRenewalTime
        : DEFAULT_TOKEN_EXPIRATION_RENEWAL_TIME;

      const userManagerConfig: UserManagerSettings = {
        client_id: REACT_APP_IDENTITY_CLIENT_ID,
        redirect_uri: redirectUri,
        authority: authorization_endpoint,
        post_logout_redirect_uri: logoffRedirectUri,
        loadUserInfo: false,
        response_type: 'code',
        scope: 'openid profile offline',
        client_secret: REACT_APP_IDENTITY_CLIENT_SECRET,
        // @ts-ignore
        client_authentication: 'client_secret_basic',
        automaticSilentRenew: true,
        redirectNavigator: new IFrameNavigator(),
        accessTokenExpiringNotificationTime: tokenRenewalTime,
        metadata: {
          issuer,
          authorization_endpoint,
          token_endpoint,
          end_session_endpoint,
          revocation_endpoint,
        },
      };

      const userManager = new UserManager(userManagerConfig);
      userManager.events.addUserLoaded((user: User) => {
        store.dispatch(
          LoginActions.refreshToken({
            workerId: user.profile.sub,
            sessionId: user.access_token,
          }),
        );
      });
      setUserManager(userManager);
      setLoading(false);
    });
  }, []);

  const signinPopup = useCallback(
    (saveToken: boolean = false) => {
      const popupFeatures = `location=no,toolbar=no,width=${window.innerWidth},height=${window.innerHeight};`;
      localStorage.setItem('redirectUri', window.location.pathname);

      userManager.stopSilentRenew();
      return userManager.getUser().then(async originalUser => {
        const newUser = await userManager.signinPopup({
          popupWindowFeatures: popupFeatures,
        });
        if (!saveToken) {
          if (originalUser) {
            await userManager.storeUser(originalUser);
          } else {
            await userManager.removeUser();
          }
        } else {
          store.dispatch(LoginActions.setRefreshSubject(newUser.profile.sub));
        }
        userManager.startSilentRenew();
        return newUser;
      });
    },
    [userManager],
  );

  const signinPopupCallback = useCallback(() => {
    return userManager.signinPopupCallback();
  }, [userManager]);

  const isAuthenticated = () => {
    // TODO: implement... maybe call to secure BFF endpoint to verify token
    return true;
  };

  const logout = useCallback(async () => {
    // This isn't working for some reason, keeping it here for reference
    // await userManager.signoutRedirect({
    //   id_token_hint: localStorage.getItem('id_token'),
    // });
    // anyways we should remove the user. Bye
    await userManager.removeUser();
    // await userManager.clearStaleState();
  }, [userManager]);

  const signOutCallback = useCallback(() => {
    return userManager.signoutPopupCallback();
  }, [userManager]);

  const getUser = useCallback(async (): Promise<string> => {
    let user = '';
    user = await userManager.getUser().then((data: User | null) => {
      const profile = data?.profile;
      const subName = profile?.sub;
      user = subName?.split('@')[1] || '';
      return user;
    });
    return user;
  }, [userManager]);

  const memoizedValue = useMemo(
    () => ({
      loading,
      signinPopup,
      signinPopupCallback,
      signOutCallback,
      isAuthenticated,
      logout,
      getUser,
    }),
    [loading, signinPopup, signinPopupCallback, logout, getUser, signOutCallback],
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
};
